/**
 * html2canvas (v1) cannot parse modern CSS color functions — it throws
 * "Attempting to parse an unsupported color function 'oklch'" the moment a
 * captured element computes to oklch / oklab / lch / lab / color(). Tailwind and
 * other libraries emit these now, so before a capture we walk the cloned subtree
 * and rewrite every such colour to a plain rgb(): the value is painted to a 1×1
 * canvas and the resolved sRGB pixel is read back (the browser does the maths).
 *
 * Call it from an html2canvas `onclone` on the element(s) being exported.
 */
const UNSUPPORTED = /\b(oklch|oklab|lch|lab|color|color-mix)\s*\(/i;
const COLOR_PROPS = [
    'color', 'backgroundColor',
    'borderTopColor', 'borderRightColor', 'borderBottomColor', 'borderLeftColor',
    'outlineColor', 'textDecorationColor', 'columnRuleColor', 'caretColor',
    'fill', 'stroke',
];
// Properties whose value can carry colour functions inline (gradients, shadows).
const COLOR_HOLDERS = ['backgroundImage', 'boxShadow', 'borderImageSource'];

let probeCtx = null;
function toRgb(value) {
    if (!value || !UNSUPPORTED.test(value)) return null;
    if (!probeCtx) {
        const cv = document.createElement('canvas');
        cv.width = 1;
        cv.height = 1;
        probeCtx = cv.getContext('2d', { willReadFrequently: true });
    }
    try {
        probeCtx.clearRect(0, 0, 1, 1);
        probeCtx.fillStyle = '#000';
        probeCtx.fillStyle = value; // invalid strings are ignored → stays #000, caught below
        probeCtx.fillRect(0, 0, 1, 1);
        const [r, g, b, a] = probeCtx.getImageData(0, 0, 1, 1).data;
        return a === 255 ? `rgb(${r}, ${g}, ${b})` : `rgba(${r}, ${g}, ${b}, ${(a / 255).toFixed(3)})`;
    } catch {
        return null;
    }
}

// Replace each non-nested colour-function token inside a longer value (a gradient
// or a shadow) with its rgb equivalent, leaving the rest of the value intact.
function rewriteTokens(value) {
    return value.replace(/\b(oklch|oklab|lch|lab|color)\([^()]*\)/gi, (m) => toRgb(m) || m);
}

/**
 * Rewrite unsupported colour functions to rgb across `root` and its descendants.
 * @param {HTMLElement} root
 */
export function sanitizeExportColors(root) {
    if (!root) return;
    const doc = root.ownerDocument || document;
    const view = doc.defaultView || window;
    const els = [root, ...root.querySelectorAll('*')];
    for (const el of els) {
        let cs;
        try { cs = view.getComputedStyle(el); } catch { continue; } // eslint-disable-line no-continue
        for (const p of COLOR_PROPS) {
            const rgb = toRgb(cs[p]);
            if (rgb) el.style[p] = rgb;
        }
        for (const p of COLOR_HOLDERS) {
            const v = cs[p];
            if (v && UNSUPPORTED.test(v)) el.style[p] = rewriteTokens(v);
        }
    }
}
